import { TZDate } from '@date-fns/tz';
import {
  format,
  formatDistanceToNow,
  isValid,
  parseISO,
  type Locale,
} from 'date-fns';

/**
 * Common date format patterns used across the application.
 * All dates should ideally be formatted in the BFF layer,
 * but these utilities exist for cases where frontend formatting is needed.
 */
export const DATE_FORMATS = {
  /** MM/dd/yyyy - e.g., 01/15/2025 */
  SHORT: 'MM/dd/yyyy',
  /** dd/MM/yyyy - e.g., 15/01/2025 (day-first; the app default is month-first SHORT) */
  SHORT_DAY_FIRST: 'dd/MM/yyyy',
  /** MMM dd, yyyy - e.g., Jan 15, 2025 */
  LONG: 'MMM dd, yyyy',
  /** MM/dd/yyyy HH:mm - e.g., 01/15/2025 14:30 */
  SHORT_WITH_TIME: 'MM/dd/yyyy HH:mm',
  /** MMM dd, yyyy - HH:mm - e.g., Jan 15, 2025 - 14:30 */
  LONG_WITH_TIME: 'MMM dd, yyyy - HH:mm',
  /** hh:mm:ss a - e.g., 02:30:45 PM */
  TIME_12H: 'hh:mm:ss a',
  /**
   * yyyy-MM-dd - e.g., 2026-08-18. The `CalendarDate` wire shape, and the form
   * that sorts chronologically as a string — use it when a day has to be
   * embedded in an identifier (a filename, a cache key) rather than displayed.
   */
  ISO_DAY: 'yyyy-MM-dd',
} as const;

export interface FormatDateOptions {
  /** date-fns format pattern (default: MM/dd/yyyy) */
  formatPattern?: string;
  /** Value to return if date is invalid (default: '-') */
  fallback?: string;
  /** date-fns Locale for localized formatting */
  locale?: Locale;
}

/**
 * Safely parses a date value (string, Date, or undefined) into a Date object.
 * @returns Valid Date object or null if invalid/undefined
 */
export function parseDate(date: Date | string | undefined | null): Date | null {
  if (!date) return null;

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj) ? dateObj : null;
  } catch {
    return null;
  }
}

/**
 * Formats a date using the specified format pattern with optional locale support.
 * @param date - Date string, Date object, or undefined
 * @param options - Formatting options (formatPattern, fallback, locale)
 * @returns Formatted date string or fallback value
 *
 * @example
 * // Basic usage
 * formatDate(new Date()) // "01/15/2025"
 *
 * @example
 * // With options
 * formatDate(date, { formatPattern: DATE_FORMATS.LONG, locale: de })
 */
export function formatDate(
  date: Date | string | undefined | null,
  options: FormatDateOptions = {},
): string {
  const {
    formatPattern = DATE_FORMATS.SHORT,
    fallback = '-',
    locale,
  } = options;

  const parsedDate = parseDate(date);
  if (!parsedDate) return fallback;

  try {
    return format(parsedDate, formatPattern, { locale });
  } catch {
    return fallback;
  }
}

/**
 * Like {@link formatDate}, but renders the pattern in the given **clinic
 * timezone** instead of the viewer's local zone. Pass the timezone from
 * `transmissionGroup.clinicTimeZone`.
 */
export function formatDateInTimeZone(
  date: Date | string | undefined | null,
  timeZone: string | undefined,
  options: FormatDateOptions = {},
): string {
  const {
    formatPattern = DATE_FORMATS.SHORT_WITH_TIME,
    fallback = '-',
    locale,
  } = options;

  const parsedDate = parseDate(date);
  if (!parsedDate) return fallback;

  try {
    return format(new TZDate(parsedDate, timeZone || 'UTC'), formatPattern, {
      locale,
    });
  } catch {
    return fallback;
  }
}

/**
 * An instant re-expressed in `timeZone`'s **wall clock**, as a mutable `Date`.
 *
 * Every getter and setter on the result speaks that zone: `getHours()` returns
 * the clinic's hour, `setHours(9)` means 09:00 there, and `toISOString()` still
 * yields the correct UTC instant to persist. That makes it the tool for EDITING
 * a stored UTC instant in a clinic's local time — `formatDateInTimeZone` only
 * renders, it cannot round-trip an edit.
 *
 * Without it, a picker reads and writes the viewer's browser zone: a New York
 * clinic's 10:58 session shows as 16:58 to a UTC+2 reviewer, and any edit they
 * save is shifted by the offset.
 *
 * Lives here rather than in an app because `@date-fns/tz` is a dependency of
 * this lib; apps reach timezone behaviour through these helpers, never by
 * importing the vendor package directly.
 *
 * @param timeZone IANA zone. Undefined falls back to UTC, matching
 * {@link formatDateInTimeZone}.
 */
export function zonedDate(
  value: Date | string | number,
  timeZone: string | undefined,
): Date {
  return new TZDate(new Date(value), timeZone || 'UTC');
}

/**
 * The instant a picked calendar day starts in `timeZone`.
 *
 * The `date` is read for its LOCAL calendar parts, not its instant — date
 * pickers hand back a `Date` at the viewer's own midnight, so the day it names
 * is what matters. Do not pass a real timestamp.
 */
export function zonedStartOfDay(
  date: Date | undefined | null,
  timeZone: string | undefined,
): Date | undefined {
  return buildZonedMidnight(readCalendarParts(date), timeZone);
}

/**
 * The LAST instant of a picked calendar day in `timeZone`.
 */
export function zonedEndOfDay(
  date: Date | undefined | null,
  timeZone: string | undefined,
): Date | undefined {
  const parts = readCalendarParts(date);
  if (!parts) return undefined;

  const [year, month, day] = parts;
  const nextMidnight = buildZonedMidnight(
    // Pure calendar arithmetic through UTC, so month/year rollover can't be
    // perturbed by a DST shift in the viewer's own zone.
    readUtcParts(new Date(Date.UTC(year, month, day + 1))),
    timeZone,
  );
  return nextMidnight ? new Date(nextMidnight.getTime() - 1) : undefined;
}

type CalendarParts = [year: number, month: number, day: number];

/** The picked day, read off `date`'s LOCAL parts. */
function readCalendarParts(
  date: Date | undefined | null,
): CalendarParts | undefined {
  if (!date || !isValid(date)) return undefined;
  return [date.getFullYear(), date.getMonth(), date.getDate()];
}

/** The same, for a `Date` built as a UTC calendar anchor. */
function readUtcParts(date: Date): CalendarParts {
  return [date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()];
}

/**
 * Midnight on the given calendar day in `timeZone`, as a plain `Date` (a real
 * UTC instant).
 *
 * An unparseable IANA name yields a NaN-valued `TZDate` rather than throwing;
 * that degrades to UTC midnight. `undefined` is reserved for a missing or
 * unparseable DATE.
 */
function buildZonedMidnight(
  parts: CalendarParts | undefined,
  timeZone: string | undefined,
): Date | undefined {
  if (!parts) return undefined;
  const [year, month, day] = parts;
  const zoned = new TZDate(year, month, day, 0, 0, 0, 0, timeZone || 'UTC');
  return isValid(zoned)
    ? new Date(zoned.getTime())
    : new Date(Date.UTC(year, month, day));
}

/** A bare FHIR `date` with no time or offset, e.g. '1959-01-01'. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A calendar date: `YYYY-MM-DD`, with no time and no timezone.
 *
 * How every date-of-birth reaches the browser. Mirrors the BFF's `CalendarDate`.
 * Keep it a string: render it with {@link formatCalendarDate}, compare it with
 * `<`/`>` (the format sorts chronologically), and send it back verbatim. The
 * ONLY place it may become a `Date` is at the edge of a calendar widget, via
 * {@link calendarDateToLocalDate} / {@link localDateToCalendarDate}.
 */
export type CalendarDate = string;

/**
 * True when `value` is a real `YYYY-MM-DD` calendar date.
 *
 * The shape alone is not enough: '1990-02-31' and '2026-13-01' match the pattern
 * but name no day. The round-trip through UTC rejects them — an impossible date
 * parses to `Invalid Date`, and a rolled-over one comes back as a different
 * string — so a malformed value is caught here instead of becoming a `NaN` that
 * travels. Mirrors the BFF's `isCalendarDate`.
 */
export function isCalendarDate(value: unknown): value is CalendarDate {
  if (typeof value !== 'string' || !DATE_ONLY_PATTERN.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
  );
}

/**
 * Shifts a calendar date by whole days, staying a calendar date.
 *
 * Arithmetic runs in UTC, so it never drifts across a DST boundary the way
 * local-time date math does (a "+30 days" that crosses a clock change lands on
 * the wrong day, or the same day twice).
 */
export function addDaysToCalendarDate(
  value: CalendarDate | undefined | null,
  days: number,
): CalendarDate | undefined {
  if (!isCalendarDate(value)) return undefined;
  const shifted = new Date(
    Date.parse(`${value}T00:00:00Z`) + days * 24 * 60 * 60 * 1000,
  );
  return shifted.toISOString().slice(0, 10);
}

/**
 * The latest of the given calendar dates, or undefined when none are valid.
 * A plain string comparison — `YYYY-MM-DD` sorts chronologically.
 */
export function maxCalendarDate(
  ...values: Array<CalendarDate | undefined | null>
): CalendarDate | undefined {
  const valid = values.filter(isCalendarDate);
  return valid.length > 0 ? valid.reduce((a, b) => (a > b ? a : b)) : undefined;
}

/**
 * Today as a calendar date in `timeZone` (IANA name; defaults to the viewer's
 * own zone, which is the right "today" for a user-facing date bound).
 */
export function todayCalendarDate(timeZone?: string): CalendarDate {
  // en-CA renders ISO-ordered parts (YYYY-MM-DD).
  return new Intl.DateTimeFormat('en-CA', {
    ...(timeZone ? { timeZone } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Calendar date -> a `Date` at LOCAL midnight, for handing to a date-picker
 * widget (they all speak `Date`).
 *
 * Local, not UTC, on purpose: the picker renders and compares in local time, so
 * a UTC-midnight Date would highlight the previous day for any viewer west of
 * UTC. Exactly inverse to {@link localDateToCalendarDate}, so a value that
 * round-trips through the widget untouched comes back identical in every zone.
 */
export function calendarDateToLocalDate(
  value: CalendarDate | undefined | null,
): Date | undefined {
  if (!isCalendarDate(value)) return undefined;
  // 'T00:00:00' without a 'Z' is parsed as local time by spec.
  const parsed = new Date(`${value}T00:00:00`);
  return isValid(parsed) ? parsed : undefined;
}

/**
 * A date-picker's `Date` -> a calendar date, read from its LOCAL parts.
 *
 * `toISOString()` would be wrong here: the picker produces local midnight, so
 * going through UTC drops a day for viewers east of UTC. Inverse of
 * {@link calendarDateToLocalDate}.
 */
export function localDateToCalendarDate(
  date: Date | undefined | null,
): CalendarDate | undefined {
  if (!date || !isValid(date)) return undefined;
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a **calendar date** — a day, not an instant.
 *
 * FHIR day-precision dates (`Patient.birthDate`, implant date, enroll date) are
 * stored at UTC midnight, so {@link formatDate} — which renders in the viewer's
 * local zone — shows the **previous day** for anyone west of UTC. A patient born
 * on 1959-01-01 reads as 12/31/1958 in America/New_York.
 *
 * Use this for anything that is conceptually "a date on a calendar"; keep
 * {@link formatDate} for real instants (timestamps). Mirrors the BFF's
 * `formatCalendarDate`, which truncates in UTC for the same reason.
 *
 * @example
 * formatCalendarDate('1959-01-01T00:00:00.000Z') // "01/01/1959" in every timezone
 */
export function formatCalendarDate(
  date: Date | string | undefined | null,
  options: FormatDateOptions = {},
): string {
  // A bare FHIR `date` ('1959-01-01') carries no offset, and `parseISO` reads it
  // as LOCAL midnight — rendering that in UTC then falls back a day for every
  // viewer EAST of UTC (Albania included). Anchor it explicitly first.
  const utcAnchored =
    typeof date === 'string' && DATE_ONLY_PATTERN.test(date)
      ? `${date}T00:00:00.000Z`
      : date;

  return formatDateInTimeZone(utcAnchored, 'UTC', {
    formatPattern: DATE_FORMATS.SHORT,
    ...options,
  });
}

/**
 * Parses a **calendar date** — a day, not an instant — into a LOCAL `Date`.
 *
 * The parse counterpart of {@link formatCalendarDate}. FHIR day-precision dates
 * reach the browser as UTC-midnight instants ('1959-01-01T00:00:00.000Z'),
 * because that is how they are stored; {@link parseDate} therefore hands back an
 * instant that a date picker shows, and a `YYYY-MM-DD` serializer writes back, as
 * the PREVIOUS day for every viewer west of UTC. This reads the day off the value
 * in UTC and rebuilds it as local midnight, so the picker, `format` and the
 * serializer all agree on which day it is.
 *
 * Use it wherever a calendar-date field becomes editable form state or feeds a
 * date computation; keep {@link parseDate} for real instants (timestamps).
 *
 * @example
 * // In America/New_York, for a patient born 1959-01-01:
 * parseDate('1959-01-01T00:00:00.000Z')         // Dec 31 1958 19:00 local
 * parseCalendarDate('1959-01-01T00:00:00.000Z') // Jan  1 1959 00:00 local
 */
export function parseCalendarDate(
  date: Date | string | undefined | null,
): Date | null {
  if (!date) return null;

  // A bare FHIR `date` ('1959-01-01') carries no offset, so its parts ARE the
  // calendar day — build local midnight from them rather than routing through
  // UTC, which would misread it for viewers east of UTC.
  if (typeof date === 'string' && DATE_ONLY_PATTERN.test(date)) {
    const [year, month, day] = date.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = parseDate(date);
  if (!parsed) return null;
  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
  );
}

/**
 * Returns a relative time string (e.g., "2 years ago", "3 months ago")
 * @param date - Date to format
 * @param options - Formatting options
 */
export function formatDateRelative(
  date: Date | string | undefined | null,
  options: Pick<FormatDateOptions, 'fallback' | 'locale'> = {},
): string {
  const { fallback = '-', locale } = options;
  const parsedDate = parseDate(date);
  if (!parsedDate) return fallback;

  try {
    return formatDistanceToNow(parsedDate, { addSuffix: true, locale });
  } catch {
    return fallback;
  }
}
